import { Image, Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import Millimetre from "../../Math/Units/Metric/Millimetre";
import { DataRow } from "../DataRow";
import InvoiceTableBlankSpace from "../InvoiceTableBlankSpace";
import druckKunstoffImageEN from "../../../images/20231009_Druckpfahl Kunststoff_EN.png";
import druckKunstoffImageDE from "../../../images/20231009_Druckpfahl Kunststoff_DE.png";
import druckKunstoffImageES from "../../../images/20231009_Druckpfahl Kunststoff_ES.png";
import { styles, tableStyles } from "../SmartTitanProjektPDF";

export function GermanyPfahlkopfDruckKunststoff({ data }) {
  const { t, i18n } = useTranslation("translation");
  const druckKunstoffImage =
    i18n.language === "de-DE"
      ? druckKunstoffImageDE
      : i18n.language === "es-ES"
        ? druckKunstoffImageES
        : druckKunstoffImageEN;
  let itemIndex = 1;
  return (
    <View wrap={true}>
      <View wrap={false}>
        {/* Mindestabmessungen und Position des Übergangsrohrs */}
        <Text style={styles.sectionSubText}>{t("aeussre.minDimensionPositionTransitionPipe")}</Text>
        <View style={tableStyles.tableContainer}>
          {/* Außendurchmesser */}
          <DataRow
            item={{
              title: t("aeussre.minDiameterTransitionPipe"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.außendurchmesser,

              // D_{min}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}D_{min}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}D_{min}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}D_{min}",
                height: 7,
                width: 18,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
              // D \geq D_a+2\cdot c_{req}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}D&space;\geq&space;D_a&plus;2\cdot&space;c_{req}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}D&space;\\geq&space;D_a&plus;2\\cdot&space;c_{req}
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}D&space;\\geq&space;D_a&plus;2\\cdot&space;c_{req}",
                height: 9,
                width: 64,
              },
            }}
          />

          {/* Wandstärke */}
          <DataRow
            item={{
              title: t("aeussre.minWallThicknessTransitionPipe"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.wandstaerke,

              // t_{min}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}t_{min}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}t_{min}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}t_{min}",
                height: 7,
                width: 14,
              },
              valueScale: 1,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
            }}
          />

          {/* Einbindelänge im Fundament */}
          <DataRow
            item={{
              title: t("aeussre.embedmentLengthFoundation"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.einbindelaengeFundament,

              // K_{min}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}K_{min}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}K_{min}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}K_{min}",
                height: 7,
                width: 18,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
            }}
          />

          {/* Einbindelänge im Baugrund */}
          <DataRow
            item={{
              title: t("aeussre.embedmentLengthBuildingGround"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.einbindelaengeBaugrund,

              // L_{b, min}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}L_{b,&space;min}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{b,&space;min}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{b,&space;min}",
                height: 9,
                width: 22,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
            }}
          />

          {/* Gesamtlänge */}
          <DataRow
            item={{
              title: t("aeussre.totalLengthTransitionPipe"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.gesamtlaenge,

              // L_{min}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}L_{min}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{min}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{min}",
                height: 7,
                width: 16,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
              // L_{min}\geq K_{min}+L_{b, min}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}L_{min}\geq&space;K_{min}&plus;L_{b,&space;min}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{min}\\geq&space;K_{min}&plus;L_{b,&space;min}
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{min}\\geq&space;K_{min}&plus;L_{b,&space;min}",
                height: 9,
                width: 80,
              },
            }}
          />
        </View>

        {/* Mindestabmessungen und Position der Kopfplatte */}
        <Text style={styles.sectionSubText}>{t("aeussre.DimensionsPositionHeadPlate")}</Text>
        <View style={tableStyles.tableContainer}>
          {/* Kantenlänge */}
          <DataRow
            item={{
              title: t("aeussre.lengthWasherPlate"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.kantenlaenge,
              // U
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}U
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}U
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}U",
                height: 6,
                width: 6,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
            }}
          />

          {/* Plattendicke */}
          <DataRow
            item={{
              title: t("aeussre.thicknessWasherPlate"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.plattendicke,
              // V
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}V
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}V
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}V",
                height: 6,
                width: 6,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
            }}
          />

          {/* Abstand zum Übergangsrohr */}
          <DataRow
            item={{
              title: t("aeussre.distanceHeadPlateTransitionTube"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.abstandUebergangsrohr,
              // A_{min}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}A_{min}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}A_{min}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}A_{min}",
                height: 7,
                width: 18,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
            }}
          />

          {/* Randabstand (Pfahlachse - Fundamentkante) */}
          <DataRow
            item={{
              title: t("aeussre.minEdgeDistance"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.randabstand,
              // R_{min}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}R_{min}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{min}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{min}",
                height: 7,
                width: 18,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
              // R\geq 1,5\cdot D+0,5\cdot U
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}R\geq&space;1,5\cdot&space;D&plus;0,5\cdot&space;U
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R\\geq&space;1,5\\cdot&space;D&plus;0,5\\cdot&space;U
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R\\geq&space;1,5\\cdot&space;D&plus;0,5\\cdot&space;U",
                height: 8,
                width: 77,
              },
            }}
          />

          {/* Achsabstand (Pfähle untereinander) */}
          <DataRow
            item={{
              title: t("aeussre.minWheelbase"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.achsabstand,
              // X_{min}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}X_{min}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}X_{min}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}X_{min}",
                height: 7,
                width: 18,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
              // X\geq 3\cdot D+U
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}X\geq&space;3\cdot&space;D&plus;U
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}X\\geq&space;3\\cdot&space;D&plus;U
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}X\\geq&space;3\\cdot&space;D&plus;U",
                height: 7,
                width: 52,
              },
            }}
          />
          <InvoiceTableBlankSpace rowsCount={5} />
        </View>
      </View>
      <View wrap={false}>
        {/* Zusatzbewehrungen B500A oder B500B */}
        <Text style={styles.sectionSubText}>{t("aeussre.additionalReinforcement")}</Text>
        {/* Obere Zusatzbewehrung */}
        <Text style={styles.sectionSubText2}>{t("aeussre.additionalUpperReinforcement")}</Text>

        <View style={tableStyles.tableContainer}>
          {/* Phi */}
          <DataRow
            item={{
              //title: t("aeussre.lengthWasherPlate"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.ObereZusatzbewehrung.phi,

              // \Phi
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\Phi
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\Phi
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\Phi",
                height: 6,
                width: 6,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
            }}
          />

          {/* n */}
          <DataRow
            item={{
              //title: t("aeussre.thicknessWasherPlate"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.ObereZusatzbewehrung.n,

              // n
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}n
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}n
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}n",
                height: 4,
                width: 5,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <>{t("inner.unitlagen")}</>,
            }}
          />

          {/* Emin */}
          <DataRow
            item={{
              //title: t("aeussre.distanceHeadPlateTransitionTube"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.ObereZusatzbewehrung.eMin,
              // E_{min}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}E_{min}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}E_{min}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}E_{min}",
                height: 7,
                width: 18,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}E_{min}=3\\cdot&space;D&plus;U",
                height: 7,
                width: 63,
              },
            }}
          />

          {/* B */}
          <DataRow
            item={{
              //title: t("aeussre.minEdgeDistance"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.ObereZusatzbewehrung.b,
              // B
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}B
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}B
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}B",
                height: 6,
                width: 6,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
              // B=D
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}B=D
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}B=D
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}B=D",
                height: 6,
                width: 25,
              },
            }}
          />
          {/* <InvoiceTableBlankSpace rowsCount={25 - itemIndex} /> */}
        </View>

        {/* Untere Zusatzbewehrung */}
        <Text style={styles.sectionSubText2}>{t("aeussre.additionalLowerReinforcement")}</Text>

        <View style={tableStyles.tableContainer}>
          {/* Phi */}
          <DataRow
            item={{
              //title: t("aeussre.lengthWasherPlate"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.UntereZusatzbewehrung.phi,
              // \Phi
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\Phi
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\Phi
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\Phi",
                height: 6,
                width: 6,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
            }}
          />

          {/* n */}
          <DataRow
            item={{
              //title: t("aeussre.thicknessWasherPlate"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.UntereZusatzbewehrung.n,
              // n
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}n
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}n
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}n",
                height: 5,
                width: 4,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <>{t("inner.unitlagen")}</>,
            }}
          />

          {/* C */}
          <DataRow
            item={{
              // title: t("aeussre.minEdgeDistance"),
              value: data.Germany.PileHeadConstruction.DruckHDPE.UntereZusatzbewehrung.c,
              // C
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}C
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}C
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}C_{min}",
                height: 7,
                width: 17,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
              // C=D+U
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}C=D&plus;U
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}C=D&plus;U
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}C_{min}=D&plus;U",
                height: 7,
                width: 53,
              },
            }}
          />
          {/* <InvoiceTableBlankSpace rowsCount={25 - itemIndex} /> */}
        </View>
      </View>
      <View style={{ alignItems: "center", marginTop: 30 }} wrap={true}>
        <Image style={{ width: "300px" }} src={druckKunstoffImage} />
      </View>
    </View>
  );
}
